import { useQueries } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { useFlags } from '../../launchdarkly.ts'
import { useImpersonation } from '../impersonation.ts'
import { useSubscription } from '../service.ts'
import { currentUserData } from '../userProfile.ts'
import { lightningProtectionsQuery } from './lightning.ts'
import { protectionsQuery } from './protections.ts'
import type { AnyProtection, ProtectionType } from './types.ts'
import { userConfigurableAssetProtectionTypes } from './utils.ts'

export const useProtections = () => {
	const impersonation = useImpersonation()
	const { data: subscription } = useSubscription()
	const hasProtectionsSub = subscription.features.includes('protections')
	return useQueries({
		queries: [
			protectionsQuery(impersonation, { enabled: hasProtectionsSub }),
			lightningProtectionsQuery(impersonation, { enabled: hasProtectionsSub }),
		],
		combine: (queries) => {
			const all = queries.flatMap<AnyProtection>((q) => q.data ?? [])
			const assetProtections = Object.groupBy(all, (p) => p.assetVrn)
			const protections = Object.entries(Object.groupBy(all, (p) => p.vrn)).reduce(
				(acc, [vrn, protList]) => {
					if (protList?.[0]) acc[vrn] = protList?.[0]
					return acc
				},
				{} as Record<string, (typeof all)[0]>,
			)

			return {
				isPending: queries.some((q) => q.isPending),
				isLoading: queries.some((q) => q.isLoading),
				totalProtections: all.length,
				assetProtections,
				protections,
			}
		},
	})
}

export const useGetProtectionsByAsset = (vrn: string) => {
	const protections = useProtections()
	return useMemo(() => protections.assetProtections[vrn] ?? [], [protections, vrn])
}

export const useIsMaxProtectedAssetsReached = () => {
	const { assetProtections } = useProtections()
	const { data: subscription } = useSubscription()
	return useMemo(() => Object.keys(assetProtections).length >= subscription.maxProtectedAssets, [assetProtections, subscription])
}

export const useIsAssetFullyProtected = () => {
	const { assetProtections } = useProtections()
	const { precipitationAlerts } = useFlags()
	return useCallback(
		(assetVrn: string) => {
			const prots = assetProtections[assetVrn] ?? []
			return userConfigurableAssetProtectionTypes
				.filter((type) => type !== 'precipitation' || precipitationAlerts)
				.every((type) => prots.some((p) => p.type === type))
		},
		[assetProtections, precipitationAlerts],
	)
}

export const useCanAddProtectionToAsset = () => {
	const { assetProtections } = useProtections()
	const { data: currentUser } = currentUserData.use()
	const maxProtLimitReached = useIsMaxProtectedAssetsReached()
	const isFullyProtected = useIsAssetFullyProtected()
	return useCallback(
		(assetVrn: string): { canAdd: boolean; reason: 'isFullyProtected' | 'maxProtectedAssets' | undefined } => {
			if (currentUser.isSuperAdmin) return { canAdd: true, reason: undefined }
			if (isFullyProtected(assetVrn)) return { canAdd: false, reason: 'isFullyProtected' }
			const prots = assetProtections[assetVrn] ?? []
			if (maxProtLimitReached && prots.length === 0) return { canAdd: false, reason: 'maxProtectedAssets' }
			return { canAdd: true, reason: undefined }
		},
		[assetProtections, maxProtLimitReached, currentUser, isFullyProtected],
	)
}

export const useCanAddProtectionTypeToAsset = () => {
	const { assetProtections } = useProtections()
	const { data: currentUser } = currentUserData.use()
	const maxProtLimitReached = useIsMaxProtectedAssetsReached()
	return useCallback(
		(assetVrn: string, type: ProtectionType): { canAdd: boolean; reason: 'alreadyHasType' | 'maxProtectedAssets' | undefined } => {
			if (type === 'webRelay') return { canAdd: currentUser.isSuperAdmin, reason: undefined }
			const prots = assetProtections[assetVrn] ?? []
			if (prots.some((p) => p.type === type)) return { canAdd: false, reason: 'alreadyHasType' }
			if (maxProtLimitReached && prots.length === 0) return { canAdd: false, reason: 'maxProtectedAssets' }
			return { canAdd: true, reason: undefined }
		},
		[assetProtections, maxProtLimitReached, currentUser],
	)
}
